import { api } from './client';

/**
 * Plot Dossier API — story-level counterpart to dossiers.js.
 * Routes live under /api/stories/{id}/plot-dossier* and
 * /api/plot-dossier-entries/{id}*. Writer-facing, not flag-gated.
 */
export const plotDossiersApi = {
  get: (storyId) => api.get(`/stories/${storyId}/plot-dossier`),

  status: (storyId) => api.get(`/stories/${storyId}/plot-dossier/status`),

  injectionForm: (storyId) => api.get(`/stories/${storyId}/plot-dossier/injection-form`),

  generate: (storyId) => api.post(`/stories/${storyId}/plot-dossier/generate`, {}),

  refresh: (storyId, { model, force } = {}) =>
    api.post(`/stories/${storyId}/plot-dossier/refresh`, { model, force }),

  confirm: (storyId, { entryIds = null, field = null } = {}) =>
    api.post(`/stories/${storyId}/plot-dossier/confirm`, {
      entry_ids: entryIds,
      field,
    }),

  edit: (entryId, content) =>
    api.patch(`/plot-dossier-entries/${entryId}`, { content }),

  accept: (entryId) => api.post(`/plot-dossier-entries/${entryId}/accept`, {}),

  reject: (entryId) => api.post(`/plot-dossier-entries/${entryId}/reject`, {}),

  extract: (storyId, {
    threadId = null, mode = 'generate', entryId = null, gapFill = null, sourceMessageId = null,
  } = {}) =>
    api.post(`/stories/${storyId}/plot-dossier/extract`, {
      thread_id: threadId,
      mode,
      entry_id: entryId,
      gap_fill: gapFill,
      source_message_id: sourceMessageId,
    }),

  addThread: (storyId, name) =>
    api.post(`/stories/${storyId}/plot-dossier/threads`, { name }),

  renameThread: (storyId, threadId, name) =>
    api.patch(`/stories/${storyId}/plot-dossier/threads/${threadId}`, { name }),

  confirmThread: (storyId, entryIds, altitudes = null) =>
    api.post(`/stories/${storyId}/plot-dossier/confirm-thread`, {
      entry_ids: entryIds,
      altitudes,
    }),

  editThreadEntry: (entryId, content) =>
    api.patch(`/plot-dossier-thread-entries/${entryId}`, { content }),

  discardThread: (storyId, { entryIds = null, threadId = null, all = false } = {}) =>
    api.post(`/stories/${storyId}/plot-dossier/discard-thread`, {
      entry_ids: entryIds,
      thread_id: threadId,
      all,
    }),

  deleteThreadEntry: (storyId, entryId) =>
    api.delete(`/stories/${storyId}/plot-dossier/thread-entries/${entryId}`),

  assignThreadEntry: (storyId, entryId, { altitude = null, outlineSectionId = null } = {}) =>
    api.post(`/stories/${storyId}/plot-dossier/thread-entries/${entryId}/assign`, {
      altitude,
      outline_section_id: outlineSectionId,
    }),

  setFramework: (storyId, frameworkRef, options = {}) =>
    api.post(`/stories/${storyId}/plot-dossier/framework`, {
      framework_ref: frameworkRef,
      ...(options.keepParts ? { keep_parts: true } : {}),
      ...(options.clearKept ? { clear_kept: true } : {}),
    }),

  dismissFrameworkDrift: (storyId) =>
    api.post(`/stories/${storyId}/plot-dossier/framework/dismiss-drift`, {}),

  setGapFill: (storyId, enabled) =>
    api.post(`/stories/${storyId}/plot-dossier/gap-fill`, { enabled }),

  audit: (storyId) =>
    api.post(`/stories/${storyId}/plot-dossier/audit`, {}),

  openAuditFocus: (storyId) =>
    api.post(`/stories/${storyId}/plot-dossier/audit/open-focus`, {}),

  plotReady: (storyId) => api.get(`/stories/${storyId}/plot-ready`),

  plotWorkshop: (storyId) => api.get(`/stories/${storyId}/plot-workshop`),

  acceptWorkshopBeat: (storyId, { beatName, content, replace = false, scenes } = {}) =>
    api.put(`/stories/${storyId}/plot-workshop/beats`, {
      beat_name: beatName,
      content,
      replace,
      ...(scenes ? { scenes } : {}),
    }),

  confirmSceneStubs: (storyId, { beatName, stubs, replace = false, earlyExit = false } = {}) =>
    api.put(`/stories/${storyId}/plot-workshop/beats/scene-stubs`, {
      beat_name: beatName,
      stubs,
      replace,
      early_exit: earlyExit,
    }),

  openWorkshopBeat: (storyId, beatName) =>
    api.post(`/stories/${storyId}/plot-workshop/beats/open`, {
      beat_name: beatName,
    }),

  sequencePreview: (storyId, sessionId) =>
    api.get(`/stories/${storyId}/plot-focus/sequence-preview?session_id=${encodeURIComponent(sessionId)}`),

  saveSequencePreview: (storyId, {
    sessionId, sequenceName, scenes, suggestions, dismissed,
  }) =>
    api.put(`/stories/${storyId}/plot-focus/sequence-preview`, {
      session_id: sessionId,
      sequence_name: sequenceName || '',
      scenes,
      // #1342: omitted keeps what the server has.
      ...(suggestions ? { suggestions } : {}),
      ...(dismissed ? { dismissed } : {}),
    }),

  generateSequencePreview: (storyId, { sessionId, sequenceName, mode = 'replace' }) =>
    api.post(`/stories/${storyId}/plot-focus/sequence-preview/generate`, {
      session_id: sessionId,
      sequence_name: sequenceName || '',
      mode,
    }),

  approveSequencePreview: (storyId, {
    sessionId, sequenceName, scenes, confirmProse = false,
  }) =>
    api.post(`/stories/${storyId}/plot-focus/sequence-preview/approve`, {
      session_id: sessionId,
      sequence_name: sequenceName || '',
      scenes,
      confirm_prose: confirmProse,
    }),
};

export default plotDossiersApi;
